<!--
  Items that can be reordered by drag and drop,
  represented as boxes with a title, a reorder button and a delete button

  Props:
  - group: all items with the same groups can be reordered with each other
  - index: the index of the item in the group
  - title: the text in the title bar, can also be used as a slot
  - icon: an icon that goes with the title
  - disabled: true to disable reordering and deleting
  - newItem: true for a new item, that cannot be reordered,
      and has a add button instead of a delete button

  Emits:
  - delete: when delete button is pressed
  - move: when the item has been drag&dropped
  - activate: when the item is clicked
-->

<template>
  <div :class="['orderable-box', {
    'orderable-box--dragged': dragged,
    'orderable-box--drop-top': targeted === -1,
    'orderable-box--drop-bottom': targeted === 1,
  }]"
    @click="handleActivate"
    @dragover="handleDragover"
    @dragleave="handleDragleave"
    @drop="handleDrop">
    <el-form-errors class="orderable-box--header-errors">
      <el-button-group class="orderable-box--header">
        <el-button style="padding: 12px;" type="info"
          icon="el-icon-d-caret"
          :draggable="disabled || newItem ? 'false' : 'true'"
          @dragstart.native="handleDragstart"
          @dragend.native="handleDragend"
          @mousedown.native="handleMousedown"
          @mouseup.native="handleMouseup"
          :disabled="disabled || newItem"
          :tabindex="-1">
            {{index + 1}}
        </el-button>
        <slot name="title">
          <div class="orderable-box--title">
            <i v-if="icon" :class="icon">&nbsp;</i>
            <slot name="content">{{title}}</slot>
            <tooltip-icon v-if="tooltip || $slots.tooltip"
              placement="top">
              <slot name="tooltip">{{ tooltip }}</slot>
            </tooltip-icon>
          </div>
        </slot>
        <el-button
          :icon="newItem ? 'el-icon-circle-plus-outline' : 'el-icon-delete'"
          :native-type="newItem ? 'xsubmitx' : null"
          :disabled="disabled"
          @click="handleDelete"
          :tabindex="-1"></el-button>
      </el-button-group>
    </el-form-errors>
    <div class="orderable-box--body">
      <slot></slot>
    </div>
  </div>
</template>

<script>
import $ from 'jquery'

import ElFormErrors from '../element/ElFormErrors.vue'

// mimetype used
let TYPE = 'orderable-box/drag'
// a global object to know who's being dragged
let DRAGGED = { group: null, index: null }

export default {
  name: 'OrderableBox',
  components: {
    ElFormErrors,
  },
  props: {
    // identifies items that are in the same array
    group: String,
    // index number of this item
    index: Number,
    // title of the item
    title: String,
    // icon of the item
    icon: String,
    // text for the tooltip
    tooltip: String,
    // disable delete and drag
    disabled: Boolean,
    // add submit button
    newItem: Boolean,
  },
  data () {
    return {
      dragged: false,
      targeted: 0,
      DRAGGED: DRAGGED,
    }
  },
  watch: {
    'DRAGGED.group' (val) {
      if (val !== this.group) this.targeted = 0
    },
  },
  methods: {
    // sets dataTransfer to start draging
    handleDragstart (e) {
      if (this.disabled || this.newItem) return
      e.dataTransfer.setData(TYPE, 'true')
      e.dataTransfer.dropEffect = 'move'
      this.DRAGGED.group = this.group
      this.DRAGGED.index = this.index
      this.dragged = true
    },
    // empties DRAGGED
    handleDragend (e) {
      this.DRAGGED.group = null
      this.DRAGGED.index = null
      this.dragged = false
    },
    // stops showing it
    handleDragleave (e) {
      if (this.disabled || this.newItem) return
      if (!e.relatedTarget ||
        e.relatedTarget.parentNode.nodeType !== Node.ELEMENT_NODE ||
        $(e.relatedTarget).closest(this.$el).length) return
      this.targeted = 0
    },
    // acceptss the item and shows where the item will be droped
    handleDragover (e) {
      if (this.disabled || this.newItem) return
      if (DRAGGED.group !== this.group || DRAGGED.index === this.index) return
      let types = e.dataTransfer.types
      if (!types.find(v => v === TYPE)) return
      e.preventDefault()
      e.dataTransfer.dropEffect = 'move'
      this.targeted = Math.sign(this.index - DRAGGED.index)
    },
    // emits 'move' event to move the item
    handleDrop () {
      if (this.disabled || this.newItem) return
      this.targeted = 0
      this.$emit('move', DRAGGED.index, this.index)
    },
    // starts dragging effect
    handleMousedown () {
      if (this.disabled || this.newItem) return
      this.dragged = true
    },
    // stops dragging effect
    handleMouseup () {
      this.dragged = false
    },
    // in case of the inner item needs an "active" state
    handleActivate (activate) {
      this.$emit('activate', activate !== false)
    },
    // emits 'delete' event
    handleDelete () {
      if (this.disabled || this.newItem) return
      this.$emit('delete')
    },
  },
}
</script>

<style lang="scss">
@import "../../element-variables.scss";

/* little space between items */
.orderable-box {
  margin: 16px 0;
  box-shadow: 0 0 5px -5px $--border-color-base;
  transition: box-shadow 0.3s ease-in-out;
}
/* for is item beng dragged */
.orderable-box.orderable-box--dragged {
  opacity: 0.5;
}
/* shows insert before */
.orderable-box.orderable-box--drop-top {
  box-shadow: 0 -16px 5px -5px $--border-color-base;
}
/* shows insert after */
.orderable-box.orderable-box--drop-bottom {
  box-shadow: 0 16px 5px -5px $--border-color-base;
}
.orderable-box--title {
  flex: 1 1 50px;
  border: 1px solid $--border-color-base;
  border-radius: 4px;
  height: $--input-height - 2px;
  line-height: $--input-height - 2px;
  padding: 0 15px;
  color: $--color-info;
  font-weight: bold;
  font-size: $--font-size-medium;
}
/* style of the title in the button group */
.el-button-group > .orderable-box--title:not(:first-child) {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}
.el-button-group > .orderable-box--title:not(:first-child) {
  margin-left: -1px;
}
.el-button-group > .orderable-box--title:not(:last-child) {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}
.el-button-group.orderable-box--header > * {
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
}
.orderable-box--header-errors > div:nth-child(2) {
  border-left: 1px solid $--border-color-base;
  border-right: 1px solid $--border-color-base;
}
.orderable-box--body {
  border: 1px solid $--border-color-base;
  border-top: 0;
  border-radius: 0 0 4px 4px;
  padding: 15px;
}
</style>
